/**
 * Peer discovery.
 *
 * Three modes:
 *
 *   - Bootstrap DHT: seeded with a hardcoded list of public peers. Used for
 *     initial join only; subsequent discovery happens peer-to-peer.
 *   - Invite codes: out-of-band shared (QR code, NFC, side channel).
 *     Encodes a PeerId + pre-shared symmetric key + expiry.
 *   - Rendezvous: subscribe to a known public topic to find peers interested
 *     in the same context. Lowest privacy, easiest UX.
 */

import { bytesToHex, utf8ToBytes } from '@noble/hashes/utils';
import { base58 } from '@scure/base';

import type { PeerId } from '../transport/Transport';

export interface InviteCode {
  version: 1;
  peer: PeerId;
  /** Symmetric pre-shared key — single use. */
  psk: string;
  /** Unix epoch seconds. */
  expiresAt: number;
  /** Optional rendezvous context. */
  context?: string;
}

export interface DiscoveryProvider {
  /** Look up peers matching a discovery query. */
  find(query: { topic?: string; pubkey?: string }): Promise<PeerId[]>;

  /** Announce this node as available for a topic. */
  announce(topic: string): Promise<void>;

  /** Generate a single-use invite. */
  createInvite(opts?: { context?: string; ttlSeconds?: number }): Promise<InviteCode>;

  /** Redeem an invite code. */
  redeemInvite(code: InviteCode): Promise<PeerId>;
}

// ─────────────────────────────────────────────────────────────────────────────
// INVITE CODE ENCODING
// ─────────────────────────────────────────────────────────────────────────────

export function encodeInvite(invite: InviteCode): string {
  const json = JSON.stringify(invite);
  const bytes = utf8ToBytes(json);
  return base58.encode(bytes);
}

export function decodeInvite(code: string): InviteCode {
  const bytes = base58.decode(code);
  const json = new TextDecoder().decode(bytes);
  const invite = JSON.parse(json) as InviteCode;
  if (invite.version !== 1) {
    throw new Error(`Unsupported invite version: ${invite.version}`);
  }
  if (invite.expiresAt * 1000 < Date.now()) {
    throw new Error('Invite expired');
  }
  return invite;
}

// ─────────────────────────────────────────────────────────────────────────────
// MOCK DISCOVERY
// ─────────────────────────────────────────────────────────────────────────────

export class MockDiscovery implements DiscoveryProvider {
  #peers: PeerId[] = [];

  async find(_query: { topic?: string; pubkey?: string }): Promise<PeerId[]> {
    return this.#peers;
  }

  async announce(_topic: string): Promise<void> { /* noop */ }

  async createInvite(opts?: { context?: string; ttlSeconds?: number }): Promise<InviteCode> {
    const psk = bytesToHex(crypto.getRandomValues(new Uint8Array(32)));
    const code: InviteCode = {
      version: 1,
      peer: { id: 'mock-peer', address: '/mock/' + Date.now() },
      psk,
      expiresAt: Math.floor(Date.now() / 1000) + (opts?.ttlSeconds ?? 3600),
    };
    if (opts?.context !== undefined) {
      code.context = opts.context;
    }
    return code;
  }

  async redeemInvite(code: InviteCode): Promise<PeerId> {
    return code.peer;
  }
}

// TODO(beta): KadDhtDiscovery — wraps @libp2p/kad-dht
// TODO(beta): WakuRendezvousDiscovery — subscribe to /surface-mesh/find/{topic}
